import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { getPlans } from "@/api/plans";
import { getStyles } from "@/api/styles";
import type { Plan, Style } from "@/types/api";
import { event as gaEvent } from "@/lib/gtag";
import StepIndicator from "./components/StepIndicator";
import PlanSelection from "./components/PlanSelection";
import PhotoUpload from "./components/PhotoUpload";
import CustomerInfo from "./components/CustomerInfo";
import StyleToneSelection from "./components/StyleToneSelection";
import Payment from "./components/Payment";
import SelectionSummary from "./components/SelectionSummary";

export default function Order() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [bridePhotos, setBridePhotos] = useState<File[]>([]);
  const [groomPhotos, setGroomPhotos] = useState<File[]>([]);
  const [brideUploadData, setBrideUploadData] = useState<{ uploadIdxList: number[]; fileKeys: string[] }>({ uploadIdxList: [], fileKeys: [] });
  const [groomUploadData, setGroomUploadData] = useState<{ uploadIdxList: number[]; fileKeys: string[] }>({ uploadIdxList: [], fileKeys: [] });
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
  });
  // 선택된 스타일 ID 배열
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);

  // API 데이터 페칭
  const { data: plans, loading: plansLoading } = useFetch(getPlans);
  const { data: styles, loading: stylesLoading } = useFetch(getStyles);

  // 선택된 플랜에서 스타일 최대 개수 추출
  const selectedPlanData = plans?.find((p: Plan) => p.code === selectedPlan);
  const planLimits: Record<string, number> =
    plans?.reduce(
      (acc: Record<string, number>, p: Plan) => ({
        ...acc,
        [p.code]: p.styleCount,
      }),
      {},
    ) || {};

  const stepNames = ['', 'plan_selection', 'photo_upload', 'customer_info', 'style_selection', 'payment'];

  const handleNext = () => {
    const nextStep = Math.min(currentStep + 1, 5);
    gaEvent('order_step', { step: nextStep, step_name: stepNames[nextStep], plan: selectedPlan });
    setCurrentStep(nextStep);
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedPlan !== "";
      case 2:
        return (
          bridePhotos.length >= 2 && brideUploadData.uploadIdxList.length >= 2 &&
          groomPhotos.length >= 2 && groomUploadData.uploadIdxList.length >= 2
        );
      case 3:
        return (
          customerInfo.name !== "" &&
          customerInfo.phone !== "" &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)
        );
      case 4: {
        const required = planLimits[selectedPlan] || 1;
        return selectedStyles.length === required;
      }
      default:
        return true;
    }
  };

  const showSummary = currentStep === 4 && selectedPlan !== "";

  // 데이터 로딩 중
  if (plansLoading || stylesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF5EA] to-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#C9A96E]"></i>
          <p className="text-sm text-gray-500 mt-3">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF5EA] to-white overflow-x-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <h1
              onClick={() => navigate("/")}
              className="font-serif text-2xl font-medium text-[#2D2D2D] cursor-pointer hover:text-[#C9A96E] transition-colors duration-300"
            >
              봄날 AI 웨딩
            </h1>
            <div className="text-sm text-gray-500">주문하기</div>
          </div>
        </div>
      </header>

      {/* Step Indicator */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <StepIndicator currentStep={currentStep} totalSteps={5} />
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div
          className={`grid ${showSummary ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1"} gap-8`}
        >
          {/* Main Content */}
          <div className={`min-w-0 ${showSummary ? "lg:col-span-2" : ""}`}>
            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-6 md:p-8">
              {currentStep === 1 && (
                <PlanSelection
                  plans={plans || []}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                />
              )}
              {currentStep === 2 && (
                <PhotoUpload
                  bridePhotos={bridePhotos}
                  groomPhotos={groomPhotos}
                  setBridePhotos={setBridePhotos}
                  setGroomPhotos={setGroomPhotos}
                  onUploadsComplete={(data) => {
                    setBrideUploadData(data.bride);
                    setGroomUploadData(data.groom);
                  }}
                />
              )}
              {currentStep === 3 && (
                <CustomerInfo info={customerInfo} setInfo={setCustomerInfo} />
              )}
              {currentStep === 4 && (
                <StyleToneSelection
                  selectedPlan={selectedPlan}
                  planLimits={planLimits}
                  styles={styles || []}
                  selectedStyles={selectedStyles}
                  setSelectedStyles={setSelectedStyles}
                />
              )}
              {currentStep === 5 && (
                <Payment
                  plans={plans || []}
                  styles={styles || []}
                  bridePhotoCount={bridePhotos.length}
                  groomPhotoCount={groomPhotos.length}
                  selectedPlan={selectedPlan}
                  selectedStyles={selectedStyles}
                  customerInfo={customerInfo}
                  uploadedS3Keys={[...brideUploadData.fileKeys, ...groomUploadData.fileKeys]}
                />
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-gray-200">
                {currentStep > 1 ? (
                  <button
                    onClick={handleBack}
                    className="px-6 py-3 text-gray-600 hover:text-[#C9A96E] transition-colors duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    이전 단계
                  </button>
                ) : (
                  <div />
                )}
                {currentStep < 5 && (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`px-8 py-3 rounded-full font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${
                      canProceed()
                        ? "bg-[#C9A96E] text-white hover:bg-[#B8956A] shadow-lg hover:shadow-xl cursor-pointer"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    다음 단계
                    <i className="ri-arrow-right-line"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Selection Summary Sidebar */}
          {showSummary && (
            <div className="lg:col-span-1 min-w-0">
              <div className="sticky top-8">
                <SelectionSummary
                  plans={plans || []}
                  styles={styles || []}
                  selectedPlan={selectedPlan}
                  selectedStyles={selectedStyles}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
